<template>
  <div>
    <tool-bar :title="title"></tool-bar>

    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <div class="margin_top_20">
      <div class="input_container" v-if="isFieldNeeded('bank_identifier')">
        <weex-text class="input_label">{{getFieldLabel('bank_identifier')}}</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('bank_identifier')"
          @input="onInputEntered('bank_identifier',$event.target.value)"
          return-key-type="next">
        <field-error-item
          :show-error="showFieldError('bank_identifier')"
          :error-msg="getErrorMsg('bank_identifier')"
        ></field-error-item>
      </div>

      <div class="input_container" v-if="isFieldNeeded('mobile_number')">
        <weex-text class="input_label">{{getFieldLabel('mobile_number')}}</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('mobile_number')"
          @input="onInputEntered('mobile_number',$event.target.value)"
          type="number"
          maxlength="10"
          return-key-type="next">
        <field-error-item
          :show-error="showFieldError('mobile_number')"
          :error-msg="getErrorMsg('mobile_number')"
        ></field-error-item>
      </div>

      <div class="input_container" v-if="isFieldNeeded('debit_card_number')">
        <weex-text class="input_label">{{getFieldLabel('debit_card_number')}}</weex-text>
        <div class="item-card-number-container">
          <div class="item-masked-card-number-div" @click="focusOnDebitCardInput">
            <input
              :disabled="true"
              class="item-masked-card-number-input"
              :value="getMaskedDebitCardNumberPrefix"
            />
          </div>

          <input
            ref="debitCardInput"
            class="item-card-last-digits-input"
            :value="getFieldValue('debit_card_number')"
            @input="onInputEntered('debit_card_number',$event.target.value)"
            type="number"
            maxlength="4"
            return-key-type="next">
        </div>
        <field-error-item
          :show-error="showFieldError('debit_card_number')"
          :error-msg="getErrorMsg('debit_card_number')"
        ></field-error-item>
      </div>


      <div class="input_container" v-if="isFieldNeeded('customer_reference_number')">
        <weex-text class="input_label">{{getFieldLabel('customer_reference_number')}}</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('customer_reference_number')"
          @input="onInputEntered('customer_reference_number',$event.target.value)"
          type="number"
          return-key-type="next">
        <field-error-item
          :show-error="showFieldError('customer_reference_number')"
          :error-msg="getErrorMsg('customer_reference_number')"
        ></field-error-item>
      </div>

      <wxc-button
        text="Next"
        class="horizontal_center margin_top_40 margin-bottom-20"
        type="blue"
        @wxcButtonClicked="onSubmit"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import ToolBar from "../layout/ToolBar";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import Loading from "../layout/Loading";
import StageCommentItem from "../layout/StageCommentItem";
import { Activities } from "../../utils/ActivityNavigator";
import DataSource from "../../data/DataSource";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import AlertService from "../core/alert/AlertService";
import EzUtils from "../../utils/EzUtils";
import DynamicFieldUtils, { FieldTypes } from "../../utils/DynamicFieldUtils";
import { ValidationPatterns } from "../../utils/ValidationPatterns";
import FieldErrorItem from "../layout/FieldErrorItem";
import { DataTables } from "../../utils/DataTables";

const DEBIT_CARD_NUMBER_PREFIX = "XXXX-XXXX-XXXX-";
const VENDOR_COLUMN_NAME = "vendor";

const ALL_FIELDS_DATA = {
  bank_identifier: {
    key: "bank_identifier",
    label: "Customer Number (Provided by bank)",
    validation: ValidationPatterns.ALPHA_NUMERIC_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid Customer Number.",
    optional: false
  },

  mobile_number: {
    key: "mobile_number",
    label: "Mobile Number",
    validation: ValidationPatterns.PHONE_NUMBER_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid Mobile Number.",
    optional: false
  },

  debit_card_number: {
    key: "debit_card_number",
    label: "Debit Card (Last four digits)",
    validation: ValidationPatterns.FOUR_DIGIT_NUMBER_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid Debit Card Number.",
    optional: false
  },

  customer_reference_number: {
    key: "customer_reference_number",
    label: "Customer Reference Number",
    validation: ValidationPatterns.NUMBER_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid Customer Reference Number.",
    optional: false
  }
};

const CLIENT_ACCOUNT_DATA_DETAILS = {
  bank_identifier: { type: "Loyalty Card", account_number: "", vendor: "" },
  mobile_number: { type: "Customer Mobile", account_number: "", vendor: "" },
  debit_card_number: { type: "Bank Debit Card", account_number: "", vendor: "" },
  customer_reference_number: { type: "Customer Reference Number", account_number: "", vendor: "" }
};

export default {
  name: "BankIdentifierActivity",
  components: {
    ActivityRedirectItem,
    ToolBar,
    WxcButton,
    Loading,
    ErrorAlertItem,
    FieldErrorItem,
    StageCommentItem
  },
  data: () => ({
    title: "Bank Identifier Number",
    loading: false,
    networkError: {},
    errorAlertEvent: false,
    dynamicFields: null,
    bankIdentifierDetails: {
      bank_identifier: null,
      mobile_number: null,
      debit_card_number: null,
      customer_reference_number: null,
    },
    invalidFields: []
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    showToast(message) {
      AlertService.showToast(message);
    },

    isFieldNeeded(key) {
      return this.dynamicFields == null || this.dynamicFields[key] != null;
    },

    getFieldLabel(key) {
      if (this.dynamicFields && this.dynamicFields[key] && this.dynamicFields[key]["label"]) {
        return this.dynamicFields[key]["label"];
      }
      return ALL_FIELDS_DATA[key]["label"];
    },

    getFieldValue(key) {
      return this.bankIdentifierDetails ? this.bankIdentifierDetails[key] : "";
    },

    showFieldError(fieldKey) {
      return this.invalidFields && this.invalidFields.includes(fieldKey);
    },

    getErrorMsg(fieldKey) {
      return ALL_FIELDS_DATA[fieldKey]["errorMsg"];
    },

    onInputEntered(key, value) {
      this.bankIdentifierDetails[key] = value;
      this.removeFromInvalidFieldsIfValid(key);
    },

    focusOnDebitCardInput() {
      if (this.$refs && this.$refs.debitCardInput) {
        this.$refs.debitCardInput.focus();
      }
    },

    removeFromInvalidFieldsIfValid(key) {
      this.invalidFields = DynamicFieldUtils.removeFromInvalidFieldsIfValidValue(
        this.invalidFields,
        key,
        this.bankIdentifierDetails[key],
        ALL_FIELDS_DATA,
        this.dynamicFields
      );
    },

    onSubmit() {
      if (this.checkForValidation()) {
        const clientAccounts = [];
        for (let [clientAccountKey, clientAccountDetails] of
          Object.entries(CLIENT_ACCOUNT_DATA_DETAILS)
          ) {
          if (this.bankIdentifierDetails[clientAccountKey]) {
            const clientAccount = clientAccountDetails;
            let accountNumber = this.bankIdentifierDetails[clientAccountKey];
            if (clientAccountKey === ALL_FIELDS_DATA.debit_card_number.key) {
              accountNumber = DEBIT_CARD_NUMBER_PREFIX + accountNumber;
            }
            clientAccount.account_number = accountNumber;
            clientAccount.vendor = this.getLender;
            clientAccounts.push(clientAccount);
          }
        }
        const noOfNewClientAccounts = clientAccounts.length;
        if (noOfNewClientAccounts > 0) {
          this.loading = true;
          DataSource.getDataOfDataTableInfoWithFilter(
            DataTables.CLIENT_ACCOUNTS,
            LoanScopeDataHolder.getClientId(),
            VENDOR_COLUMN_NAME,
            this.getLender
          )
            .then(clientAccountsRes => {
              if (clientAccountsRes.errorOccurred) {
                this.showError(clientAccountsRes.data);
              } else {
                const existingClientAccounts = clientAccountsRes.data;
                let noOfSavedNewClientAccounts = 0;
                clientAccounts.forEach(newClientAccount => {
                  const existingClientAccount = this.getExistingClientAccount(
                    existingClientAccounts,
                    newClientAccount.type
                  );
                  if (existingClientAccount && existingClientAccount.id) {
                    DataSource.updateDataTableEntryInMultiRowDataTable(
                      DataTables.CLIENT_ACCOUNTS,
                      LoanScopeDataHolder.getClientId(),
                      existingClientAccount.id,
                      newClientAccount
                    )
                      .then(clientAccountUpdateRes => {
                        this.loading = false;
                        if (clientAccountUpdateRes.errorOccurred) {
                          this.showError(clientAccountUpdateRes.data);
                        } else {
                          noOfSavedNewClientAccounts++;
                          if (noOfSavedNewClientAccounts === noOfNewClientAccounts) {
                            this.completeCurrentTaskAndGetNextTask();
                          }
                        }
                      })
                  } else {
                    DataSource.addDataTableEntry(
                      DataTables.CLIENT_ACCOUNTS,
                      LoanScopeDataHolder.getClientId(),
                      newClientAccount
                    )
                      .then(clientAccountCreateRes => {
                        this.loading = false;
                        if (clientAccountCreateRes.errorOccurred) {
                          this.showError(clientAccountCreateRes.data)
                        } else {
                          noOfSavedNewClientAccounts++;
                          if (noOfSavedNewClientAccounts === noOfNewClientAccounts) {
                            this.completeCurrentTaskAndGetNextTask();
                          }
                        }
                      })
                  }
                })
              }
            })
        } else {
          this.completeCurrentTaskAndGetNextTask();
        }
      }
    },

    getExistingClientAccount(clientAccounts, accountType) {
      if (!accountType || !clientAccounts || clientAccounts.length === 0) {
        return null;
      }
      for (let clientAccountIndex in clientAccounts) {
        const clientAccount = clientAccounts[clientAccountIndex];
        if (clientAccount && EzUtils.areEqualString(accountType, clientAccount.type)) {
          return clientAccount;
        }
      }
      return null;
    },

    checkForValidation() {
      this.invalidFields = DynamicFieldUtils.validateDynamicFieldsForRequiredFields(
        this.bankIdentifierDetails,
        ALL_FIELDS_DATA,
        this.dynamicFields
      );

      return !this.invalidFields || this.invalidFields.length === 0;
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          }
        });
    }
  },

  created: function () {
    this.dynamicFields = DynamicFieldUtils.getRequiredDynamicFieldsData();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.BANK_IDENTIFIER];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    getLender() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().lender
        : "";
    },

    getMaskedDebitCardNumberPrefix() {
      return DEBIT_CARD_NUMBER_PREFIX;
    }
  }
}
</script>

<style scoped src="../../assets/style/base.css"></style>

